import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {useAccount, usePublicClient, useSignTypedData} from 'wagmi';
import { DepositWithdrawalService } from '../services/deposit-withdrawal/deposit-withdrawal.service';
import { PositionsManager } from '../services/position/position.manager';
import { ContractContext } from './ContractsProvider';
import { ModalContext } from './ModalProvider';
import { ToasterContext } from './ToasterProvider/ToasterProvider';
import {DepositWithdrawalLockService} from "../services/deposit-withdrawal-lock/deposit-withdrawal-lock.service";
import {DepositWithdrawalEarnService} from "../services/deposit-withdrawal-earn/deposit-withdrawal-earn.service";

interface ServicesProviderProps {
    children: ReactNode;
}

export const ServicesContext = createContext({
    depositWithdrawService: new DepositWithdrawalService(),
    depositWithdrawalEarnService: new DepositWithdrawalEarnService(),
    depositWithdrawLockService: new DepositWithdrawalLockService(),
    positionsManager: new PositionsManager()
});

export const ServicesProvider: React.FC<ServicesProviderProps> = ({ children }) => {
    const publicClient = usePublicClient();
    const { usdcContract, ddpContract, nrsContract, lockNrsContract, nrsLockWithdrawalTimelockContract, lpNrsUsdcContract, lpNrsUsdcStakingContract, lpNxusdUsdcContract, lpNxusdUsdcStakingContract } = useContext(ContractContext);
    const { addToasterWithStatusAndDescription, removeToaster } = useContext(ToasterContext);
    const { closeModal } = useContext(ModalContext);
    const { address } = useAccount();
    const depositServiceRef = useRef<DepositWithdrawalService>(new DepositWithdrawalService());
    const depositWithdrawalEarnServiceRef = useRef<DepositWithdrawalEarnService>(new DepositWithdrawalEarnService());
    const depositLockServiceRef = useRef<DepositWithdrawalLockService>(new DepositWithdrawalLockService());
    const positionsManagerRef = useRef<PositionsManager>(new PositionsManager());
   const { signTypedDataAsync } = useSignTypedData();

    const [services, setServices] = useState({
        depositWithdrawService: depositServiceRef.current,
        depositWithdrawalEarnService: depositWithdrawalEarnServiceRef.current,
        depositWithdrawLockService: depositLockServiceRef.current,
        positionsManager: positionsManagerRef.current
    });

    useEffect(() => {

        if (address) {
            positionsManagerRef.current.injectDependencies({
                userAddress: address,
                addToasterWithStatusAndDescription,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);

            setServices({
                depositWithdrawService: depositServiceRef.current,
                depositWithdrawLockService: depositLockServiceRef.current,
                depositWithdrawalEarnService: depositWithdrawalEarnServiceRef.current,
                positionsManager: positionsManagerRef.current
            });
        }
    }, [address]);

    useEffect(() => {
        if (usdcContract && address && ddpContract && nrsContract && lockNrsContract && nrsLockWithdrawalTimelockContract
            && lpNrsUsdcContract && lpNrsUsdcStakingContract && lpNxusdUsdcContract && lpNxusdUsdcStakingContract) {
            depositServiceRef.current.injectDependencies({
                usdcContract,
                ddpContract,
                userAddress: address,
                addToasterWithStatusAndDescription,
                removeToaster,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);
            depositLockServiceRef.current.injectDependencies({
                publicClient,
                nrsContract,
                lockNrsContract,
                nrsLockWithdrawalTimelockContract,
                userAddress: address,
                addToasterWithStatusAndDescription,
                removeToaster,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);
            depositWithdrawalEarnServiceRef.current.injectDependencies({
                publicClient,
                nrsContract,
                lpNrsUsdcContract,
                lpNrsUsdcStakingContract,
                lpNxusdUsdcContract,
                lpNxusdUsdcStakingContract,
                userAddress: address,
                addToasterWithStatusAndDescription,
                removeToaster,
                closeModal,
                signTypedData: signTypedDataAsync
            } as any);
            setServices({
                depositWithdrawService: depositServiceRef.current,
                depositWithdrawLockService: depositLockServiceRef.current,
                depositWithdrawalEarnService: depositWithdrawalEarnServiceRef.current,
                positionsManager: positionsManagerRef.current
            });
        }

    }, [nrsContract, lockNrsContract, nrsLockWithdrawalTimelockContract, usdcContract, lpNrsUsdcContract, lpNrsUsdcStakingContract, lpNxusdUsdcContract, lpNxusdUsdcStakingContract,
        address, ddpContract, closeModal, addToasterWithStatusAndDescription]);

    return (
        <ServicesContext.Provider value={services}>
            {children}
        </ServicesContext.Provider>
    );
};

// Custom hook to use the services
export const useServices = () => {
    return useContext(ServicesContext);
};
